import React from "react";
import ResizeObserver from "resize-observer-polyfill";
// import { useDebounce } from "use-debounce";

/**
 * @deprecated
 */
export const withDefaultProps = <P extends object, DP extends Partial<P>>(
  defaultProps: DP,
  component: React.ComponentType<P>,
) => {
  type ActualProps = Partial<DP> & Pick<P, Exclude<keyof P, keyof DP>>;
  component.defaultProps = defaultProps;
  return (component as any) as React.ComponentType<ActualProps>;
};

import { useState, useEffect } from "react";

function getElementDimensions(element: Element | null) {
  if (!element) {
    return { width: 0, height: 0 };
  }

  const { clientWidth: width, clientHeight: height } = element;
  return {
    width,
    height,
  };
}

// Taken from https://usehooks.com/useDebounce/
export const useDebounceTrailing = (value: any, delay: number) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );
  return debouncedValue;
};

// interface ElementDimensionsProps {
//   element: HTMLElement | null;
// }

export const useElementDimensions = (element: HTMLElement | null, duration = 5) => {
  const [elementMounted, setElementMounted] = React.useState(!!element);
  const [elementDimensions, setElementDimensions] = useState(getElementDimensions(element));
  const debouncedElementDimensions = useDebounceTrailing(elementDimensions, duration);

  useEffect(() => {
    if (!elementMounted && element) {
      setElementMounted(true);
    }
  }, [element]);

  useEffect(() => {
    if (!elementMounted || !element) {
      return;
    }

    setElementDimensions(getElementDimensions(element));

    new ResizeObserver(() => {
      setElementDimensions(getElementDimensions(element));
    }).observe(element);
  }, [elementMounted]);

  return debouncedElementDimensions;
};

export const useWindowSize = () => {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined as undefined | number,
    height: undefined as undefined | number,
  });
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
};

export const wrapWithClassComponent = <T extends {}>(
  Component: React.FunctionComponent<T>,
): React.ComponentClass<T> => {
  return class Wrapper extends React.PureComponent<T> {
    render() {
      return <Component {...this.props} />;
    }
  };
};

export const isClassComponent = <T extends {}>(
  C: React.ComponentType<T>,
): C is React.ComponentClass<T> => {
  // Detect if a component is a class by checking for the isReactComponent flag, included on the
  // prototype by extending React.Component
  return !!C?.prototype?.isReactComponent;
};

// Key listener hook that takes in keys to listen for and a callback.
export const useKeyListener = (keys: string[], callback: (event: KeyboardEvent) => void) => {
  const keyDownHandler = (event: KeyboardEvent) => {
    if (keys.includes(event?.key)) {
      callback(event);
    }
    return true;
  };

  useEffect(() => {
    window.addEventListener("keydown", keyDownHandler);

    return () => {
      window.removeEventListener("keydown", keyDownHandler);
    };
  }, [keys, callback]);
};

export const CONFIRM_UNSAVED_ONUNLOAD_TEXT =
  "Your changes have not been saved. They will be lost if you continue.";

/**
 * Subscribes / Unsubscribes to `beforeunload` event and triggers browser warning if `isConfirmationRequired`
 * Allows users to cancel unloading and remain on page
 *
 * @remarks
 * per MDN `event.returnValue` is deprecated
 * however, currently is the only way to do this in FF, Chrome, Safari
 * additionally the browser may not respect message provided and use its own langauge
 *
 * @example
 * const [hasChanges, setChanges] = useState(false);
 * useConfirmBeforeUnloadEvent(hasChanges);
 */
export const useConfirmBeforeUnloadEvent = (isConfirmRequired: boolean) => {
  const confirmHandler = React.useCallback((event: BeforeUnloadEvent) => {
    event.returnValue = CONFIRM_UNSAVED_ONUNLOAD_TEXT;
    return CONFIRM_UNSAVED_ONUNLOAD_TEXT;
  }, []);

  React.useEffect(() => {
    if (isConfirmRequired) {
      window.addEventListener("beforeunload", confirmHandler);
    } else {
      window.removeEventListener("beforeunload", confirmHandler);
    }

    return () => {
      window.removeEventListener("beforeunload", confirmHandler);
    };
  }, [isConfirmRequired, confirmHandler]);
};
